import { T, useGT } from 'gt-react';
import { MultiSelect } from 'src/components/common/MultiSelect';
import { useUserGroupsList } from 'src/api/rpc/hooks/organization/useUserGroups';
import { FieldBlock } from './TaskTypeEditorSections';
import { viewerGroupPickerState } from './taskTypeViewerGroupPicker';

/**
 * User groups that see every task of this type. Empty keeps the personal
 * queue (assignee, creator, or watcher).
 */
export function TaskTypeViewerGroupsField({
  viewerUserGroupIds,
  disabled,
  onChange,
}: {
  viewerUserGroupIds: readonly string[];
  disabled?: boolean;
  onChange: (viewerUserGroupIds: string[]) => void;
}) {
  const gt = useGT();
  const { data: userGroups, isPending, isError } = useUserGroupsList();
  const groupsUnavailable = userGroups == null;
  const { options, selected } = viewerGroupPickerState({
    viewerUserGroupIds,
    userGroups,
    savedTeamLabel: gt('Saved team', {
      context:
        'fallback label for a queue viewer group while team names are unavailable',
    }),
  });

  return (
    <FieldBlock
      label={<T context="task type form field label">Queue viewers</T>}
      description={
        <T context="hint under the task type queue viewers field">
          Members of these groups see every task of this type, including tasks
          assigned to someone else. With no groups selected, a non-admin sees
          only tasks they are assigned, created, or watching. Gently admins
          still see every task.
        </T>
      }
    >
      <MultiSelect
        options={options}
        value={selected}
        disabled={disabled || groupsUnavailable}
        fullWidth
        onChange={(next) => onChange(next.map((option) => option.id))}
        placeholder={
          isPending
            ? gt('Loading teams…', {
                context:
                  'queue viewers picker placeholder while user groups are loading',
              })
            : gt('No one sees the whole queue', {
                context:
                  'queue viewers picker placeholder when no user groups are selected',
              })
        }
      />
      {isError ? (
        <p className="text-xs text-destructive">
          <T context="error loading user groups for the task type team picker">
            Could not load teams.
          </T>
        </p>
      ) : null}
    </FieldBlock>
  );
}
